import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../../apiBase';
import { partGenKeys } from './partsRun';
import { readState, writeState } from './docState';
import { bumpImagesEpoch, useImagesEpoch } from './imagesRefresh';

// Phase 1/3 helpers: manual info + brand detection, per-part image generation, and
// persistence of the detected info (keyed by document).

// General info read off the manual's cover page. `brand` qualifies the part
// image generation; `time` / `people` are shown when the page states them (often
// absent, hence empty strings). Persisted as one object per document.
// `reasoning` / `raw` are the model's thought summary and raw reply, surfaced in
// a hover tooltip (same as the categories table) so the extraction is inspectable.
// A normalized bounding box [x0, y0, x1, y1], 0..1 from the page's top-left.
export type HeroBox = [number, number, number, number];

// And a polygon in that same space: [x, y] pairs, 0..1 from the top-left. What a rectangle
// cannot say — which pixels inside it are the thing and which are the room behind it. Here
// beside the box because the two are one vocabulary: everything that draws one draws the
// other, and everything that reads pixels out of one reads them through the other. See
// ListingCrop.outline for where they come from.
export type Outline = [number, number][];

export type NoticeInfo = {
  brand: string;
  // The product's own commercial name (e.g. "BILLY"), headline of the public tutorial.
  productName: string;
  time: string;
  people: string;
  maxWeight: string;
  // Product category (e.g. "Chairs"), classified by the model and editable.
  category: string;
  // What "Find the links" searches the shop with: its address as the manual prints it, and
  // the phrases to type into its search box.
  site: string;
  queries: string[];
  // Where the manual draws the finished product: a page and the box on it. The picture
  // "Find the links" matches the shop's photographs against. Both null when no page does.
  heroPage: number | null;
  heroBox: HeroBox | null;
  reasoning: string;
  raw: string;
};
export const emptyInfo = (): NoticeInfo => ({
  brand: '', productName: '', time: '', people: '', maxWeight: '', category: '',
  site: '', queries: [], heroPage: null, heroBox: null, reasoning: '', raw: '',
});

// Accept a stored / returned hero box only when it is four finite numbers.
export function asHeroBox(v: unknown): HeroBox | null {
  return Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === 'number' && isFinite(n))
    ? (v as HeroBox)
    : null;
}

// The hero pair, or both null: a page with no box (or a box on no page) is no picture.
export function asHero(page: unknown, box: unknown): Pick<NoticeInfo, 'heroPage' | 'heroBox'> {
  const heroBox = asHeroBox(box);
  return typeof page === 'number' && page >= 1 && heroBox
    ? { heroPage: page, heroBox }
    : { heroPage: null, heroBox: null };
}

const infoKey = (docId: string) => `notice.info.${docId}`;
const legacyBrandKey = (docId: string) => `notice.brand.${docId}`;

export function loadInfo(docId: string): NoticeInfo {
  try {
    const o = readState<Partial<NoticeInfo> | null>(docId, infoKey(docId), null);
    if (o) {
      return {
        brand: o.brand || '',
        productName: o.productName || '',
        time: o.time || '',
        people: o.people || '',
        maxWeight: o.maxWeight || '',
        category: o.category || '',
        site: o.site || '',
        queries: Array.isArray(o.queries) ? o.queries.filter((q) => typeof q === 'string') : [],
        // Page and box are only ever meaningful together.
        ...asHero(o.heroPage, o.heroBox),
        reasoning: o.reasoning || '',
        raw: o.raw || '',
      };
    }
    // Fall back to the older brand-only key so a previously detected brand survives.
    return { ...emptyInfo(), brand: readState<string>(docId, legacyBrandKey(docId), '') };
  } catch {
    return emptyInfo();
  }
}

export function saveInfo(docId: string, info: NoticeInfo) {
  try {
    writeState(docId, infoKey(docId), info);
  } catch {
    // ignore
  }
}

// One entry in a part's generation history: the photo, the model that drew it, and — when it
// was made by regenerating an earlier attempt — the index of THAT attempt (its parent). A
// from-scratch generation has no parent. This turns the flat list into a version tree.
// WHAT A GENERATED PICTURE IS CALLED, on screen and in a sentence to somebody else.
//
// Three numbered things live on this page and they are unrelated: the manual's RUNS (a
// pass over its phases, 1 to 12, in the bar at the top), a part's MESHES (one per attempt
// at a 3D model), and these. A bare "#12" under a picture names none of them.
//
// The same word in both languages on purpose: it is an identifier, like a part reference,
// and a French reader quoting "ImgIA #12" to an English one would be quoting a different
// name for the same row.
export const AI_IMG = 'AIimg';

export type GenEntry = {
  // The notice_part_images row id, when this entry came from (or was just written to) the
  // server store. Absent only for a legacy in-memory entry that never round-tripped.
  id?: number;
  model: string;
  // THE PICTURE: the stored row's own address (see get_part_image), which the browser loads where
  // it is shown and keeps — or, for a version made since the page opened, the data URL it came
  // back as. Either goes in an <img>; what must send the pixels on reads them through asDataUrl.
  image: string;
  // The sha256 of its bytes, from the server — which Parts-DB photo it is (see
  // useGeneratedPhotos). Absent on a version made since the page opened: that one is a data URL
  // and is hashed here.
  sha?: string;
  parent?: number | null;
  // The notice listing-photo ids actually sent to the generator as context for THIS
  // attempt — exactly the ones the user kept selected in the CONTEXT row ([] = none).
  // undefined/null on attempts made before this was recorded (their real context is
  // unknown and can't be reconstructed). The schematic + hero are always sent besides.
  contextIds?: number[] | null;
  // Which side of the part this image was ASKED for — the angle chosen in the 3D
  // pipeline's regenerate dialog. Empty for every image generated without one, which is
  // all of the LLM pipeline's and everything made before this was recorded. Not defaulted
  // to 'front': "no angle was chosen" and "the front was chosen" are different facts, and
  // only the second one is worth printing under a picture.
  view?: string;
  // What the user TYPED into the regenerate dialog for this image — the free instruction,
  // not the whole prompt (the rest is built from the part's name, its category and the
  // brand, and is the same on every attempt). Empty for a plain redo, and for everything
  // made before it was recorded: it was said once into a box that then closed, so unlike
  // the schematic, the context and the angle there is nothing to reconstruct it from.
  feedback?: string;
};

// A version's place in the 2D history grid: its column (a from-scratch lineage, numbered on
// the x-axis) and its row (how many "regenerate-from" steps deep it is, lettered on the
// y-axis, 0 = the root of its column).
export type GenCell = { entry: GenEntry; index: number; col: number; row: number };

// Lay the flat, append-only history out as a grid. A version with no parent starts a new
// column at row 0; a version regenerated FROM another sits one row below its parent — in the
// parent's own column when it is the first such child, otherwise in a fresh column (a
// branch), so two children never collide on one cell. Creation order (and thus indices) is
// preserved, so a stored parent index stays valid.
export function genLayout(history: GenEntry[]): { cells: GenCell[]; cols: number } {
  const rowOf: number[] = [];
  const colOf: number[] = [];
  const hasChild: boolean[] = [];
  let nextCol = 0;
  history.forEach((entry, i) => {
    const p = entry.parent;
    if (p == null || p < 0 || p >= i) {
      rowOf[i] = 0;
      colOf[i] = nextCol++;
    } else {
      rowOf[i] = rowOf[p] + 1;
      if (!hasChild[p]) {
        colOf[i] = colOf[p];
        hasChild[p] = true;
      } else {
        colOf[i] = nextCol++;
      }
    }
  });
  return {
    cells: history.map((entry, i) => ({ entry, index: i, col: colOf[i], row: rowOf[i] })),
    cols: nextCol,
  };
}

// Phase 2's result: the GENERATED photos of each part, kept per NOTICE + PART as a
// HISTORY — {partKey: [{model, image}, …]} oldest→newest. Regenerating APPENDS, so no
// attempt is lost and the cell can slide through them; the history spans both models, so
// toggling the model never blanks the cell.
//
// The store is the server (notice_part_images), NOT run-scoped doc_state — that is the
// whole point of this: a part's real photo is a fact about the part in the notice, so
// every run shows the same photos and a fresh run no longer starts Phase 2 blank. The
// module cache below is the client mirror; it is read synchronously (loadGenerated) by
// the panel and the phase-progress bar, and filled by ensurePartImages / saveGenerated.
const genCache = new Map<string, Record<string, GenEntry[]>>();
const genLoaded = new Set<string>();
const genLoading = new Map<string, Promise<void>>();

// One server entry, tolerant of shape (it crosses the wire).
type PartImageDto = {
  id?: number; sha?: string; model?: string; parent?: number | null; contextIds?: number[] | null;
  view?: string; feedback?: string;
};

// A stored picture's address — see get_part_image.
const partImageSrc = (docId: string, id: number) =>
  apiUrl(`/api/notice/doc/${encodeURIComponent(docId)}/part-images/${id}`);

function toEntry(docId: string, e: PartImageDto): GenEntry | null {
  if (!e || typeof e.id !== 'number') return null;
  return {
    id: e.id,
    model: String(e.model ?? ''),
    image: partImageSrc(docId, e.id),
    sha: typeof e.sha === 'string' ? e.sha : undefined,
    parent: typeof e.parent === 'number' ? e.parent : null,
    contextIds: Array.isArray(e.contextIds) ? e.contextIds.filter((n) => typeof n === 'number') : null,
    view: typeof e.view === 'string' ? e.view : '',
    feedback: typeof e.feedback === 'string' ? e.feedback : '',
  };
}

// WHETHER THE STORE HAS ANSWERED for this notice. The manual opens without waiting for it (see
// NoticeViewer), so for a moment every part reads as having no photo — which a press that
// generates what is missing must not act on.
export const partImagesLoaded = (docId: string) => genLoaded.has(docId);

// This notice's generated part photos, from the client mirror. Empty until the store has
// been fetched (ensurePartImages) — the epoch bump on load makes readers recompute.
export function loadGenerated(docId: string): Record<string, GenEntry[]> {
  return genCache.get(docId) ?? {};
}

// One part's photographs out of that map, under whichever key they were written to.
//
// Never `gen[key]` directly: a part's key depends on the category ontology, which arrives
// over the network, so a key computed a moment too early is submodel-scoped where the
// photographs are shared. See partGenKeys — the fallback is the plain key, which is the
// only other key this part could ever have had.
export function generatedUnder(
  gen: Record<string, GenEntry[]>,
  key: string,
): GenEntry[] {
  for (const k of partGenKeys(key)) {
    const hit = gen[k];
    if (hit?.length) return hit;
  }
  return [];
}

// Fetch a notice's part-image store once and cache it, then bump the images epoch so the
// panel and the phase-progress bar re-read. Idempotent per notice: concurrent callers
// share one request, and a loaded notice is a no-op.
export async function ensurePartImages(docId: string): Promise<void> {
  if (!docId || genLoaded.has(docId)) return;
  let p = genLoading.get(docId);
  if (!p) {
    p = (async () => {
      try {
        const { data } = await axios.get<{ parts: Record<string, PartImageDto[]> }>(
          `/api/notice/doc/${encodeURIComponent(docId)}/part-images`,
        );
        const out: Record<string, GenEntry[]> = {};
        for (const [key, list] of Object.entries(data.parts || {})) {
          const entries = (Array.isArray(list) ? list : []).map((e) => toEntry(docId, e)).filter((e): e is GenEntry => !!e);
          if (entries.length) out[key] = entries;
        }
        genCache.set(docId, out);
        genLoaded.add(docId);
      } catch {
        // Leave the cache empty; a failed fetch shouldn't wedge the phase, and the user
        // can still generate (which repopulates it).
      } finally {
        genLoading.delete(docId);
        bumpImagesEpoch(docId);
      }
    })();
    genLoading.set(docId, p);
  }
  return p;
}

// Append one attempt to a part's history: POST it to the server store and mirror it into
// the cache. `parent` is the index (within that part's existing history) of the attempt it
// was regenerated from — null for a from-scratch image. Resolves once stored, so the
// caller can bump the epoch and re-read a cache that already holds it.
export async function saveGenerated(
  docId: string,
  ref: string,
  model: string,
  image: string,
  parent: number | null = null,
  // The listing-photo ids sent as context for this attempt, so the catalog's zoomed
  // view can later show the TRUE context instead of every listing photo.
  contextIds: number[] | null = null,
  // The angle it was asked for, when one was — see GenEntry.view.
  view?: string,
  // The instruction the user typed for this attempt — see GenEntry.feedback.
  feedback?: string,
): Promise<void> {
  // Make sure the store has finished loading BEFORE we append — otherwise an in-flight
  // ensurePartImages could resolve after this and overwrite the entry we just added.
  await ensurePartImages(docId);
  const { data } = await axios.post<{ id: number; position: number }>(
    `/api/notice/doc/${encodeURIComponent(docId)}/part-images`,
    {
      partKey: ref, image, model, parent, contextIds,
      view: view || undefined, feedback: feedback || undefined,
    },
  );
  const cur = genCache.get(docId) ?? {};
  const entry: GenEntry = {
    id: data?.id, model, image, parent, contextIds, view: view || '', feedback: feedback || '',
  };
  genCache.set(docId, { ...cur, [ref]: [...(cur[ref] ?? []), entry] });
}

// Drop one generated version. The server renumbers the survivors' `parent` (an index into
// this history, not a row id), so the cache is rebuilt the same way here rather than just
// splicing: a child of the deleted attempt becomes a root, and anything pointing past it
// shifts down one. Splicing alone would leave every later lineage drawn from the wrong
// picture until the next reload.
export async function deleteGenerated(docId: string, ref: string, index: number): Promise<void> {
  await ensurePartImages(docId);
  const cur = genCache.get(docId) ?? {};
  const list = cur[ref] ?? [];
  const entry = list[index];
  if (!entry) return;
  if (entry.id != null) {
    await axios.delete(
      `/api/notice/doc/${encodeURIComponent(docId)}/part-images/${entry.id}`,
    );
  }
  const next = list
    .filter((_, i) => i !== index)
    .map((e) => {
      const p = e.parent;
      if (p == null) return e;
      if (p === index) return { ...e, parent: null };
      return p > index ? { ...e, parent: p - 1 } : e;
    });
  genCache.set(docId, { ...cur, [ref]: next });
}

// Attach a generated photo to its catalog item, tagged with the manual it came from.
// The backend replaces this manual's previous photo of the item, so regenerating
// updates it in the catalog rather than stacking copies. Used when a generated part is
// already in the database — the photo then shows in its "Photos réelles" column.
export async function attachCatalogPhoto(
  itemId: number,
  image: string,
  docId: string,
  model: string,
  // The seq of the run this photo was generated in, so the catalog's zoomed view can name
  // it. Omitted when unknown.
  runSeq?: number | null,
  // Whether that run was a DEV run (true) or a PROD run (false) — the zoomed view's badge.
  // Omitted when unknown.
  runFromDev?: boolean | null,
  // The listing-photo ids actually sent as context for this photo, so the zoomed view
  // shows the real context and not every listing photo. Omitted when unknown (legacy).
  contextIds?: number[] | null,
  // The submodel this photo shows: its id (what the catalog filters by) and the label to
  // show for it. Both omitted for hardware and tools — the same object in every colour,
  // so they belong to no submodel and stay visible under all of them.
  variant?: { id: string; label: string } | null,
  // Which side of the part it shows — carried across from the generated version so the
  // Parts DB photo can say it too. The 3D phase reads its inputs from the catalog, and a
  // multi-view reconstructor needs the angle to slot each picture; without it they are an
  // unordered pile of one part.
  view?: string,
  // Which pipeline the run belonged to ('llm' / 'models'). Recorded rather than derived:
  // run_seq restarts per mode, so a photo's run number names two runs, one in each.
  runMode?: string,
): Promise<number> {
  const { data } = await axios.post<{ id: number }>(`/api/notice/catalog/${itemId}/images`, {
    image: await asDataUrl(image),
    docId,
    model,
    runSeq: typeof runSeq === 'number' ? runSeq : undefined,
    runFromDev: typeof runFromDev === 'boolean' ? runFromDev : undefined,
    contextIds: Array.isArray(contextIds) ? contextIds : undefined,
    variant: variant?.id || undefined,
    variantLabel: variant?.label || undefined,
    view: view || undefined,
    runMode: runMode || undefined,
  });
  return data.id;
}

// Remove one catalog photo by its id (the copy a Phase 2 toggle added).
export async function detachCatalogPhoto(imageId: number): Promise<void> {
  await axios.delete(`/api/notice/catalog/image/${imageId}`);
}

// A catalog photo generated FROM this manual: its id and the sha256 of its bytes. Phase 2
// decides membership by matching a generated version's OWN image content against these
// hashes — not a catalog-image id (sync remaps them) and not (item, run, model) (too
// coarse: two Pro versions of one part would both match the single stored photo). Content
// match is exact and self-healing.
export type DocCatalogPhoto = { id: number; hash: string };

// The catalog photos this manual produced, as a contentHash -> photo-id map.
export async function fetchDocCatalogPhotos(docId: string): Promise<Map<string, number>> {
  const { data } = await axios.get<{ photos: DocCatalogPhoto[] }>(
    `/api/notice/doc/${encodeURIComponent(docId)}/catalog-photos`,
  );
  const map = new Map<string, number>();
  for (const p of data.photos || []) {
    if (p.hash) map.set(p.hash, p.id);
  }
  return map;
}

// sha256 (hex) of a base64 data URL's decoded bytes — the SAME bytes the catalog stores,
// so it matches the server's encode(sha256(image),'hex'). Lets a generated version be
// matched to its catalog copy by content. Null on a malformed data URL.
// THE PIXELS OF A VERSION, for a press that sends them on — attaching it to the Parts DB, or
// correcting it. A stored one is an address (see GenEntry.image) and is fetched then: the bytes
// are only wanted where they leave the page, and the list no longer carries them.
export async function asDataUrl(image: string): Promise<string> {
  if (image.startsWith('data:')) return image;
  const { data } = await axios.get<Blob>(image, { responseType: 'blob', baseURL: '' });
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(data);
  });
}

export async function sha256Hex(dataUrl: string): Promise<string | null> {
  try {
    const bin = atob(dataUrl.replace(/^data:[^,]+,/, ''));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

// What the generator is given about a part: the name (already resolved to the
// database name in the reader's language) plus context that sharpens the render.
export type GenPartInput = {
  name: string;
  brand?: string;
  productName?: string;
  category?: string | null;
  size?: string | null;
  // Hardware / tool: the finished-product photo is not sent and the prompt renders the
  // standalone piece.
  hardware?: boolean;
};

// Generate one real photo of a part. `image` is the phase-2 crop (the part's
// schematic); `product` — when there is one — is the finished item's cover photo, so
// the part is rendered faithful to how it looks on the whole item. Both are data URLs;
// the returned data URL is the generated photo.
export async function generatePartImage(
  model: string,
  part: GenPartInput,
  image: string,
  // THE PART'S OTHER DRAWINGS — the crops the schema row found of it in the assembly steps,
  // as data URLs, exactly the ones left ticked in the dialog. The supplied-parts page draws
  // a part once and that one view decides everything the generator believes about it; these
  // are the same part from the angles the steps happened to need. Empty for a part nobody
  // has searched the steps for, which is every part until that button is pressed.
  schemas: string[],
  product?: string | null,
  docId?: string,
  // The listing-photo ids the user kept selected in the CONTEXT row — exactly these are
  // added as context ([] = none). Undefined lets the server fall back to the sorted set.
  contextImageIds?: number[],
  // A guided regenerate: a free instruction and/or the previous attempt to correct (kept
  // clean; a toggle decides whether it's sent). The server re-renders the whole part; any
  // region masking (keeping only the drawn zones) is done client-side on the returned image.
  correction?: {
    feedback?: string;
    previous?: string;
  } | null,
  // Which side of the part to photograph it from — one of viewDirections. May be absent:
  // undefined leaves the generator to frame the part as it likes, which is what a portrait
  // asked for on its own wants.
  view?: string,
  // WHETHER THESE PIXELS BECOME A TEXTURE. The 3D pipeline's renders are what a model's
  // texture patch is cut from — its surface colour — so they are lit flat — a highlight in one
  // stops being light and becomes the wood's own colour, in a file, for good.
  //
  // ITS OWN FLAG, and not read off `view`. That was the first way round and it was wrong: an
  // angle of "none" is a perfectly ordinary choice in this phase and says nothing about who
  // the render is for, so it silently opted the picture back into lighting meant for eyes —
  // and the empty string it sets slips through a `??` besides. What decides is which phase
  // is asking, which is a thing the caller knows outright.
  forTexture?: boolean,
): Promise<string> {
  const { data } = await axios.post<{ image: string }>('/api/notice/generate-part-image', {
    model,
    image,
    images: schemas.length ? schemas : undefined,
    product: product || undefined,
    // Lets the server pull this notice's listing photos as extra product context.
    docId: docId || undefined,
    contextImageIds,
    feedback: correction?.feedback || undefined,
    previous: correction?.previous ? await asDataUrl(correction.previous) : undefined,
    view: view || undefined,
    forTexture: forTexture || undefined,
    ...part,
  });
  return data.image;
}

// WHICH generated attempt a Parts-DB photo IS, and vice versa.
//
// The two are the same picture and neither knows it. A generated version lives in this
// notice's own history (notice_part_images, per part, with what was typed and what was
// sent); its copy in the Parts DB is a catalog row with an id of its own, attached to a
// part rather than to an attempt. Nothing links them — a stored catalog id would be wrong
// the moment a sync remapped it — so they are matched on the CONTENT, which is the only
// exact test: two Pro attempts at one part would both "match" the single stored photo by
// any looser rule.
//
// Here rather than in the phase that first needed it, because both halves of Phase 2 ask
// it now: the generation table, to say which versions are in the database, and the 3D
// panel, to say which attempt the photograph it is about to reconstruct from came from.
//
// `refresh` is anything the caller changes when a photo is added or removed — a counter is
// enough. Without it the map would still describe the database as it was before the toggle.
export function useGeneratedPhotos(docId: string, refresh = 0) {
  const epoch = useImagesEpoch(docId);
  const genMap = useMemo(
    () => loadGenerated(docId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docId, epoch],
  );

  // sha256 of every version's image, accumulated across renders: hashing is async and a
  // history grows one picture at a time, so re-hashing the lot on every render would be
  // work already done.
  const [hashes, setHashes] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    const images = new Set<string>();
    // The stored ones are hashed by the server, which has their bytes (see GenEntry.sha).
    const said: [string, string][] = [];
    for (const hist of Object.values(genMap)) {
      for (const e of hist) {
        if (e.sha) {
          if (!hashes.has(e.image)) said.push([e.image, e.sha]);
        } else images.add(e.image);
      }
    }
    if (said.length) setHashes((prev) => new Map([...prev, ...said]));
    const missing = [...images].filter((im) => !hashes.has(im) && im.startsWith('data:'));
    if (!missing.length) return;
    let cancelled = false;
    void (async () => {
      const pairs: [string, string][] = [];
      // An image that cannot be hashed is filed under '' — answered, and matching nothing —
      // so the switch below does not wait on it for ever (see dbKnown).
      for (const im of missing) pairs.push([im, (await sha256Hex(im)) ?? '']);
      if (!cancelled && pairs.length) setHashes((prev) => new Map([...prev, ...pairs]));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genMap]);

  // The catalog photos this manual produced, hash -> id. From the server, so membership is
  // derived from the catalog itself and a failed read leaves what we had rather than
  // blanking every switch on the page.
  //
  // NULL UNTIL IT HAS ANSWERED. It started as an empty Map, and an empty map says every
  // version is out of the database: the switches drew OFF, and pressing one on a version that
  // was in it sent an ADD. See dbKnown, which is what the switches wait on.
  const [docPhotos, setDocPhotos] = useState<Map<string, number> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetchDocCatalogPhotos(docId)
      .then((m) => !cancelled && setDocPhotos(m))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [docId, refresh]);

  // The reverse direction, built once per change: catalog photo id -> the attempt that
  // produced it. What the 3D panel needs, having a photograph and wanting its provenance.
  const byPhotoId = useMemo(() => {
    const out = new Map<number, GenEntry>();
    for (const hist of Object.values(genMap)) {
      for (const e of hist) {
        const h = hashes.get(e.image);
        const id = h && docPhotos ? docPhotos.get(h) : undefined;
        if (id != null && !out.has(id)) out.set(id, e);
      }
    }
    return out;
  }, [genMap, hashes, docPhotos]);

  return {
    genMap,
    // The catalog photo id whose bytes are THIS version's image, or undefined when that
    // exact image is not in the database.
    photoIdOf: (image: string | undefined): number | undefined => {
      const h = image ? hashes.get(image) : undefined;
      return h && docPhotos ? docPhotos.get(h) : undefined;
    },
    // WHETHER photoIdOf's answer for this image is one: the catalog has been read and the
    // image hashed. Until then undefined means "not asked yet", not "not in the database",
    // and a switch drawn off on it would add what it should remove.
    dbKnown: (image: string): boolean => docPhotos !== null && hashes.has(image),
    // The attempt a Parts-DB photo came from, or undefined for one uploaded by hand,
    // generated in another manual, or made before any of this was recorded.
    entryOf: (photoId: number | undefined): GenEntry | undefined =>
      (photoId == null ? undefined : byPhotoId.get(photoId)),
  };
}
